import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import { countUniqueCourseParticipants } from '../../lib/courseSignups';
import { toInputDateTime, defaultDeadline, formatDateTime } from '../../lib/examResults';

/**
 * Bottom drawer: confirm sending the course's linked exam to every participant
 * with a deadline. Prefilled from the exam's default deadline, else 2 hours from now.
 */
@observer
class CourseSendExam extends Component {
    constructor(props) {
        super(props);
        this.state = {
            deadline: '',
            sending: false,
            result: null,
            error: null,
        };
    }

    componentDidMount() {
        this.loadAll();
    }

    loadAll = async () => {
        const { id } = this.props;
        const { courseStore, examStore } = this.props.stores;
        if (id && (!courseStore.course || courseStore.course.id !== id)) {
            await courseStore.load(id, false, { query: { hostname: courseStore.currentHostname } });
        }
        const { course = {} } = courseStore;
        if (course.exam > 0) {
            await examStore.loadExam(course.exam);
            const { exam = {} } = examStore;
            const suggested = exam.deadline ? new Date(exam.deadline) : defaultDeadline();
            const useSuggested = suggested.getTime() > Date.now() ? suggested : defaultDeadline();
            this.setState({ deadline: toInputDateTime(useSuggested) });
        }
    }

    handleDeadline = (e) => {
        this.setState({ deadline: e.target.value });
    }

    send = async () => {
        const { courseStore, appState } = this.props.stores;
        const { callback = () => {} } = this.props;
        const { course = {} } = courseStore;
        const { deadline } = this.state;
        if (!deadline) {
            this.setState({ error: 'Velg en frist.' });
            return;
        }
        this.setState({ sending: true, error: null });
        try {
            const response = await courseStore.sendExam(course.id, new Date(deadline));
            if (response?.status === 200) {
                this.setState({ result: response.data });
                appState.addToast({
                    type: 'success',
                    message: `Prøven er sendt til ${response.data.added} deltaker${response.data.added === 1 ? '' : 'e'}.`,
                }, 5000);
                setTimeout(() => callback(response.data), 1500);
            } else {
                this.setState({ error: response?.message || 'Kunne ikke sende prøven.' });
            }
        } finally {
            this.setState({ sending: false });
        }
    }

    render() {
        const { courseStore, examStore } = this.props.stores;
        const { course = {} } = courseStore;
        const { exam = {} } = examStore;
        const { deadline, sending, result, error } = this.state;
        const participantCount = countUniqueCourseParticipants(course);
        const alreadyInvited = (exam.invitedUsers || []).filter(u => u && Number(u.course) === Number(course.id)).length;

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100' style='max-width: 720px; padding: 20px 16px 100px;'>
                    <h5 class='mb-1'>
                        <i class='fa-solid fa-paper-plane me-2' />
                        <Text id='course.sendExam'>Send prøve til deltakerne</Text>
                    </h5>
                    <p class='text-body-secondary small mb-3'>{course.title}</p>

                    {!(course.exam > 0) ? (
                        <div class='alert alert-warning'>
                            <Text id='course.noExamLinked'>Ingen prøve er koblet til kurset. Velg en under «Rediger».</Text>
                        </div>
                    ) : (
                        <>
                            <div class='card mb-3'>
                                <div class='card-body'>
                                    <div class='d-flex justify-content-between mb-2'>
                                        <span class='text-body-secondary'><Text id='course.examToSend'>Prøve</Text></span>
                                        <strong>{exam.title || `#${course.exam}`}</strong>
                                    </div>
                                    <div class='d-flex justify-content-between mb-2'>
                                        <span class='text-body-secondary'><Text id='course.participantsToInvite'>Deltakere på kurset</Text></span>
                                        <strong>{participantCount}</strong>
                                    </div>
                                    {alreadyInvited > 0 && (
                                        <div class='d-flex justify-content-between'>
                                            <span class='text-body-secondary'><Text id='course.alreadyInvited'>Allerede invitert fra dette kurset</Text></span>
                                            <strong>{alreadyInvited}</strong>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div class='mb-3'>
                                <label class='form-label fw-semibold' for='sendExamDeadline'>
                                    <Text id='course.examDeadline'>Frist for å ta prøven</Text>
                                </label>
                                <input
                                    id='sendExamDeadline'
                                    type='datetime-local'
                                    class='form-control'
                                    value={deadline}
                                    onInput={this.handleDeadline}
                                    disabled={sending || !!result}
                                />
                                <div class='form-text'>
                                    <Text id='course.examDeadlineHelp'>Etter fristen kan prøven ikke tas. Ved frister lenger enn ett døgn får deltakerne en påminnelse ett døgn før.</Text>
                                    {deadline && <> ({formatDateTime(new Date(deadline))})</>}
                                </div>
                            </div>

                            {error && <div class='alert alert-danger py-2'>{error}</div>}

                            {result ? (
                                <div class='alert alert-success'>
                                    <i class='fa-solid fa-circle-check me-2' />
                                    <Text id='course.examSentSummary' fields={{ added: result.added, skipped: result.skipped }}>
                                        Sendt til {result.added} deltakere. {result.skipped} var allerede invitert.
                                    </Text>
                                </div>
                            ) : (
                                <button
                                    type='button'
                                    class='btn btn-primary rounded-pill'
                                    onClick={this.send}
                                    disabled={sending || participantCount === 0}
                                >
                                    {sending ? <i class='fa-solid fa-spinner fa-spin me-1' /> : <i class='fa-solid fa-paper-plane me-1' />}
                                    <Text id='course.sendExamNow' fields={{ count: participantCount }}>Send til {participantCount} deltakere</Text>
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    }
}

export default CourseSendExam;
